/**
 * Fixture data + transport for `?fixture=plan` — a RAW `GET /api/cockpit/plan`
 * envelope in the exact shape `azure-functions/cis/shared/cockpit.js`
 * (`planView`) and `CisCockpitPlan/index.js` produce, run through the same
 * `shapePlanScreen` pipeline (src/features/sm/api/shape.js) the live app
 * uses. The upsert side (`createFixturePlanStore`) reimplements just enough
 * of `azure-functions/cis/shared/schedule.js` (`upsertScheduleItem`,
 * `SITE_MANAGER_EDITABLE`) and the `CisScheduleImport` inspector-roster gate
 * to make drag-and-drop testable offline: created/updated/duplicate/
 * conflict/invalid outcomes, so the Plan screen's read-back-never-optimistic
 * rule (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3) is exercised without a
 * network call.
 *
 * Visible text (titles, dates, ids, per-tile time of day) is copied from
 * mockups/cockpit-v3/Plan.dc.html where a real backend field carries it
 * (`plannedTime`, InspectionSchedule.PlannedTime); illustrative-only mockup
 * fields with no backend equivalent (a role label like "Hull and coating",
 * a due-date estimate on an undated row, an import-source label) are still
 * omitted rather than invented — see mockups/cockpit-v3/README.md "Sample
 * data note". `assignedTo` UPNs are fictional sample names, matching
 * fixtures/main.js.
 */

"use strict";

const WEEK_START = "2026-08-31";
const TODAY = "2026-09-02"; // Wed — matches the artboard's highlighted "today" column

// Roster membership per project — the real access boundary the upsert
// simulator checks (azure-functions/cis/shared/roster.js, CisScheduleImport
// §4.3 inspector_not_on_roster gate). k.sato is rostered on BOTH SS412413
// and SS418, the AC-02 cross-team-lending case: an SS412413 item can be
// assigned to her even though her lane is labelled with only the SS418
// team (AC-02: "labelled with team B" — the roster grants cross-project
// access; the lane label is never every project rostered).
const ROSTER = {
  SS412413: new Set(["j.mendoza@example.com", "k.sato@example.com"]),
  SS418: new Set(["t.aoki@example.com", "k.sato@example.com"]),
  SS411: new Set(["r.fields@example.com"]),
};

// Register inspection numbers a booking's sourceInspectionNumber may
// resolve to (CisScheduleImport §4.3: a missing one resolves `invalid`
// with `source_not_found`).
const REGISTER_NUMBERS = new Set([1842, 1821, 956, 3011]);

// Lane order and the single team each lane is labelled with (mockups/
// cockpit-v3/Plan.dc.html row order: Mendoza, Aoki, Fields, Sato). This is
// fixture presentation, not a re-derivation of the live planView sort (that
// sorts lanes alphabetically by UPN on the real API path) — the fixture is
// hand-authored to match the artboard's own lane sequence and labels.
const LANE_ORDER = ["j.mendoza@example.com", "t.aoki@example.com", "r.fields@example.com", "k.sato@example.com"];
const LANE_LABEL_TEAM = {
  "j.mendoza@example.com": ["SS412413"],
  "t.aoki@example.com": ["SS418"],
  "r.fields@example.com": ["SS411"],
  // First-listed team is the label (AC-02); the full list is her roster.
  "k.sato@example.com": ["SS418", "SS412413"],
};

function scheduleRow(overrides) {
  return {
    scheduleId: overrides.scheduleId,
    scheduleRef: overrides.scheduleRef,
    code: overrides.code,
    title: overrides.title,
    category: overrides.category,
    stage: overrides.stage,
    inspectionType: overrides.inspectionType,
    plannedDate: overrides.plannedDate,
    plannedTime: overrides.plannedTime || "",
    witnessParties: overrides.witnessParties || "",
    assignedTo: overrides.assignedTo || "",
    scheduleStatus: overrides.scheduleStatus || "Planned",
    linkedInspectionNumber: overrides.linkedInspectionNumber || 0,
    sourceInspectionNumber: overrides.sourceInspectionNumber || 0,
    reminderSentDate: overrides.reminderSentDate || "",
    notes: overrides.notes || "",
    importedAtUtc: overrides.importedAtUtc || "2026-08-25T07:00:00.000Z",
    importedBy: overrides.importedBy || "",
    sourceRunId: overrides.sourceRunId || "",
    etag: overrides.etag || `"etag-${overrides.scheduleRef}"`,
    // Day redesign phase 2 (optional; empty on every older row).
    ...(overrides.block ? { block: overrides.block } : {}),
    ...(overrides.side ? { side: overrides.side } : {}),
    ...(overrides.basis ? { basis: overrides.basis } : {}),
    ...(overrides.recordedInspectionNumber ? { recordedInspectionNumber: overrides.recordedInspectionNumber } : {}),
  };
}

// Owner retest 4: rows the fixture's latest yard-schedule import created.
export const FIXTURE_IMPORT_RUN_ID = "imp-ss412413-20260831t0740z";
export const fixtureImportRuns = [
  { runId: FIXTURE_IMPORT_RUN_ID, fileName: "SS412413 yard schedule wk36.xlsx", importedAt: "2026-08-31T07:40:00.000Z", importedBy: "sm@example.com", counts: { created: 36, updated: 6, duplicate: 4, ops: 42 }, firstPlannedDate: "2026-09-03" },
];

const INITIAL_ROWS = [
  scheduleRow({ scheduleId: 101, scheduleRef: "ITP-HE-041", code: "SS412413", title: "Block 42P welding, No.3 CWBT", category: "Hull Erection", stage: "Erection", inspectionType: "Witness Point", plannedDate: "2026-08-31", plannedTime: "09:00", assignedTo: "j.mendoza@example.com", scheduleStatus: "Completed", linkedInspectionNumber: 1901 }),
  scheduleRow({ scheduleId: 102, scheduleRef: "ITP-PA-052", code: "SS412413", title: "Ballast tank final, No.5 WBT", category: "Painting", stage: "Final", inspectionType: "Witness Point", plannedDate: "2026-09-02", plannedTime: "09:00", assignedTo: "j.mendoza@example.com" }),
  scheduleRow({ scheduleId: 103, scheduleRef: "ITP-HE-055", sourceRunId: FIXTURE_IMPORT_RUN_ID, code: "SS412413", title: "Block 42P seam UT witness", category: "Hull Erection", stage: "Erection", inspectionType: "Witness Point", plannedDate: "2026-09-03", plannedTime: "11:00", assignedTo: "j.mendoza@example.com" }),
  scheduleRow({ scheduleId: 104, scheduleRef: "ITP-HE-056", sourceRunId: FIXTURE_IMPORT_RUN_ID, code: "SS412413", title: "Block 43P erection joint fit-up", category: "Hull Erection", stage: "Fit-up", inspectionType: "Hold Point", plannedDate: "2026-09-04", plannedTime: "09:00", assignedTo: "j.mendoza@example.com" }),
  scheduleRow({ scheduleId: 201, scheduleRef: "ITP-MO-071", code: "SS418", title: "Main engine chock", category: "Machinery Outfitting", stage: "Alignment", inspectionType: "Hold Point", plannedDate: "2026-08-31", plannedTime: "14:00", assignedTo: "t.aoki@example.com", scheduleStatus: "Completed", linkedInspectionNumber: 1902 }),
  // "Skipped" (a real SCHEDULE_STATUSES value, validation.js) is the backend
  // equivalent of the artboard's "hold" chip — a plan slot the site manager
  // has put on hold rather than cancelled or completed.
  scheduleRow({ scheduleId: 202, scheduleRef: "ITP-QA-018", code: "SS418", title: "Yard meeting: NCR chase list", category: "Others", stage: "Coordination", inspectionType: "Monitor", plannedDate: "2026-09-01", plannedTime: "15:00", assignedTo: "t.aoki@example.com", scheduleStatus: "Skipped" }),
  scheduleRow({ scheduleId: 203, scheduleRef: "ITP-EO-033", sourceRunId: FIXTURE_IMPORT_RUN_ID, code: "SS418", title: "Cable transit sealing, ER flat", category: "Electrical Outfitting", stage: "Sealing", inspectionType: "Witness Point", plannedDate: "2026-09-02", plannedTime: "13:30", assignedTo: "t.aoki@example.com" }),
  scheduleRow({ scheduleId: 204, scheduleRef: "ITP-PA-060", code: "SS418", title: "Hold No.2 surface prep", category: "Painting", stage: "Prep", inspectionType: "Hold Point", plannedDate: "2026-09-04", plannedTime: "13:00", assignedTo: "t.aoki@example.com" }),
  scheduleRow({ scheduleId: 301, scheduleRef: "ITP-OU-011", code: "SS411", title: "A-60 insulation, deck 2", category: "Hull Outfitting", stage: "Insulation", inspectionType: "Witness Point", plannedDate: "2026-09-01", plannedTime: "10:00", assignedTo: "r.fields@example.com", scheduleStatus: "Completed", linkedInspectionNumber: 1903 }),
  scheduleRow({ scheduleId: 302, scheduleRef: "ITP-HE-066", code: "SS411", title: "Hatch coaming welds, hold 4", category: "Hull Erection", stage: "Welding", inspectionType: "Witness Point", plannedDate: "2026-09-03", plannedTime: "09:00", assignedTo: "r.fields@example.com" }),
  scheduleRow({ scheduleId: 205, scheduleRef: "CI-2026-1821", code: "SS412413", title: "Ballast DFT re-inspection", category: "Painting", stage: "Re-inspection", inspectionType: "Witness Point", plannedDate: "2026-09-04", assignedTo: "k.sato@example.com", sourceInspectionNumber: 1821 }),
];

const UNBOOKED_ROWS = [
  scheduleRow({ scheduleId: 401, scheduleRef: "ITP-HE-120", sourceRunId: FIXTURE_IMPORT_RUN_ID, code: "SS412413", title: "Block 45S erection joint", category: "Hull Erection", stage: "Erection", inspectionType: "Witness Point", plannedDate: "" }),
  scheduleRow({ scheduleId: 402, scheduleRef: "ITP-MO-074", code: "SS418", title: "Shaft alignment, final", category: "Machinery Outfitting", stage: "Alignment", inspectionType: "Hold Point", plannedDate: "" }),
  scheduleRow({ scheduleId: 403, scheduleRef: "ITP-PA-061", code: "SS411", title: "Shell final coat, hold 4", category: "Painting", stage: "Final", inspectionType: "Witness Point", plannedDate: "" }),
];

const IMPORTS = [
  { code: "SS412413", runId: "imp-ss412413-20260831t0740z", importedAtUtc: "2026-08-31T07:40:00.000Z", status: "committed_partial", itemCounts: { created: 30, updated: 6, duplicate: 4, conflict: 1, invalid: 1, skipped: 6 } },
];

// Plan redesign (owner-approved, DEC-CHAT-20261007-412): the "To book"
// backlog — planView's own shape (azure-functions/cis/shared/cockpit.js
// §4.3, PR #1958), re-inspections overdue first, then undated rows. One
// reinspection entry here exercises the "Re-inspections" filter pill and the
// drag-to-book-with-sourceInspectionNumber path offline.
const REINSPECTION_BACKLOG = [
  { kind: "reinspection", code: "SS412413", inspectionNumber: 1821, title: "Ballast DFT re-inspection, No.5 WBT", inspectionDate: "2026-08-20", ageDays: 12, assignedTo: "" },
];

function unbookedToBacklogRow(row) {
  return { kind: "unbooked", code: row.code, scheduleRef: row.scheduleRef, title: row.title, category: row.category, stage: row.stage, assignedTo: row.assignedTo };
}

/** RAW `GET /api/cockpit/plan?weekStart=2026-08-31` envelope. */
export function buildFixturePlanEnvelope({ rows = INITIAL_ROWS, unbooked = UNBOOKED_ROWS, reinspectionBacklog = REINSPECTION_BACKLOG } = {}) {
  const backlog = [...reinspectionBacklog, ...unbooked.map(unbookedToBacklogRow)];
  return {
    generatedAtUtc: "2026-09-02T09:00:00.000Z",
    weekStart: WEEK_START,
    lanes: LANE_ORDER.map((upn) => ({ upn, teams: LANE_LABEL_TEAM[upn] })).concat([{ upn: "", teams: [] }]),
    items: rows.map((row) => ({ ...row, assignedTo: String(row.assignedTo || "").toLowerCase() })),
    unbooked: unbooked.map((row) => ({ ...row, assignedTo: String(row.assignedTo || "").toLowerCase() })),
    backlog,
    backlogTotal: backlog.length,
    backlogOffset: 0,
    backlogLimit: 20,
    imports: IMPORTS,
    errors: [],
  };
}

export const fixturePlanEnvelope = buildFixturePlanEnvelope();
export const fixturePlanToday = TODAY;

/* ---------------- day redesign phase 2: the Plan with a forward schedule ---------------- */

const FC_WEEK_START = "2026-10-26"; // Mon, week 44
const FC_TODAY = "2026-10-27"; // Tue, as the Plan artboard draws it
const SMU = "t.aoki@example.com";
const ASU = "k.sato@example.com";

/** Which Plan fixture world a `?fixture=` value opens: its name, its Monday and its today. */
export function planFixtureWorld(fixture) {
  return fixture === "plan-forecast"
    ? { name: "forecast", weekStart: FC_WEEK_START, today: FC_TODAY }
    : { name: "base", weekStart: WEEK_START, today: TODAY };
}

function fcBooked(id, ref, block, side, title, date, time, assignedTo, done, recorded) {
  return scheduleRow({
    scheduleId: id,
    scheduleRef: ref,
    code: "SS418",
    title: `${block} ${title}`,
    category: "Hull Erection",
    stage: "Daily booking",
    inspectionType: "Witness Point",
    plannedDate: date,
    plannedTime: time,
    assignedTo,
    block,
    side,
    scheduleStatus: done ? "Completed" : "Planned",
    linkedInspectionNumber: recorded || 0,
    recordedInspectionNumber: recorded || 0,
  });
}

const FC_ROWS = [
  fcBooked(501, "DB-20261026-0501", "UD7", "", "final", "2026-10-26", "09:00", SMU, true, 1990),
  fcBooked(502, "DB-20261026-0502", "BG2", "", "welding", "2026-10-26", "14:00", SMU, true, 1991),
  fcBooked(503, "DB-20261026-0503", "2D3", "", "final", "2026-10-26", "10:00", ASU, true, 1992),
  fcBooked(504, "DB-20261027-0504-P", "UD10", "P", "final", "2026-10-27", "08:00", SMU, true, 2001),
  fcBooked(505, "DB-20261027-0504-S", "UD10", "S", "final", "2026-10-27", "08:00", SMU, true, 2002),
  fcBooked(506, "DB-20261027-0506", "BG7", "P", "repair check", "2026-10-27", "09:30", SMU, true, 2008),
  fcBooked(507, "DB-20261027-0507", "BS2-D", "", "cutting", "2026-10-27", "13:00", SMU, false, 0),
  fcBooked(508, "DB-20261027-0508-P", "BG3", "P", "welding", "2026-10-27", "08:00", ASU, true, 2003),
  fcBooked(509, "DB-20261027-0509", "2D5", "P", "final", "2026-10-27", "10:30", ASU, false, 0),
];

function fcForecast(id, date, block, side, title, category = "Hull Erection") {
  return scheduleRow({
    scheduleId: id,
    scheduleRef: `SD-SS418-${category === "Welding" ? "weld" : "fab"}-${block}${side ? `-${side}` : ""}`,
    code: "SS418",
    title,
    category,
    stage: "Block schedule",
    inspectionType: "Witness Point",
    plannedDate: date,
    block,
    side,
    basis: "forecast",
  });
}

const FC_STORED = [
  // Wed 28 Oct: expected (1 day after today)
  fcForecast(601, "2026-10-28", "UD15", "P", "final"), fcForecast(602, "2026-10-28", "UD15", "S", "final"),
  fcForecast(603, "2026-10-28", "UD16", "P", "final"), fcForecast(604, "2026-10-28", "3D3", "S", "final"),
  fcForecast(605, "2026-10-28", "UD18", "P", "final"), fcForecast(606, "2026-10-28", "UD18", "S", "final"),
  // Thu 29 Oct: expected (2 days)
  fcForecast(611, "2026-10-29", "BG10", "P", "welding", "Welding"), fcForecast(612, "2026-10-29", "BG10", "S", "welding", "Welding"),
  fcForecast(613, "2026-10-29", "2D7", "P", "final"), fcForecast(614, "2026-10-29", "2D7", "S", "final"),
  fcForecast(615, "2026-10-29", "3D5", "S", "final"),
  // Fri 30 and Sat 31 Oct: likely (3 and 4 days)
  fcForecast(621, "2026-10-30", "UD17", "P", "final"), fcForecast(622, "2026-10-30", "UD17", "S", "final"),
  fcForecast(623, "2026-10-30", "3D4", "P", "final"), fcForecast(624, "2026-10-30", "3D6", "P", "final"),
  fcForecast(625, "2026-10-30", "3D6", "S", "final"), fcForecast(626, "2026-10-30", "BG12", "P", "welding", "Welding"),
  fcForecast(627, "2026-10-30", "BG12", "S", "welding", "Welding"),
  fcForecast(631, "2026-10-31", "BG11", "S", "welding", "Welding"), fcForecast(632, "2026-10-31", "BG13", "P", "welding", "Welding"),
];

const FC_LATER = [
  { weekStart: "2026-11-02", sides: 24 },
  { weekStart: "2026-11-09", sides: 31 },
  { weekStart: "2026-11-16", sides: 28 },
  { weekStart: "2026-11-23", sides: 19 },
];

const FC_REPAIR_CHECKS = [
  { code: "SS418", inspectionNumber: 1960, inspectionDate: "2026-10-20", block: "UD6", side: "S", title: "block final inspection", defectText: "Gap 4 mm at frame 108" },
  { code: "SS418", inspectionNumber: 1963, inspectionDate: "2026-10-21", block: "3D4", side: "P", title: "block final inspection", defectText: "Spatter on deck plate" },
  { code: "SS418", inspectionNumber: 1971, inspectionDate: "2026-10-22", block: "BG4", side: "S", title: "welding inspection", defectText: "2 of 3 defects not repaired" },
  { code: "SS418", inspectionNumber: 1975, inspectionDate: "2026-10-23", block: "2D4", side: "P", title: "block final inspection", defectText: "Misaligned stiffener, frame 96" },
];

/** The project register behind the record detail of the forecast world (BG7 P closed the defect of 20 Oct). */
export const FC_REGISTER = [
  { inspectionNumber: 1968, etag: '"r1968"', inspectionItem: "BG7 · welding inspection", inspectionDate: "2026-10-20", category: "Hull Erection", stage: "Daily booking", result: "Not Accepted", block: "BG7", side: "P", defectNoted: true, defectText: "Undercut at frame 112", approvedBy: "", approvedAtUtc: "", repairLink: "" },
  { inspectionNumber: 2008, etag: '"r2008"', inspectionItem: "BG7 · welding inspection", inspectionDate: "2026-10-27", category: "Hull Erection", stage: "Daily booking", result: "Accepted", block: "BG7", side: "P", defectNoted: false, defectText: "", approvedBy: "", approvedAtUtc: "", repairLink: "" },
  {
    inspectionNumber: 2001,
    etag: '"r2001"',
    clientCaptureId: "5b1f3c1e-8d0a-4e5b-9a57-2001c0ffee01",
    inspectionItem: "UD10 P · block final inspection",
    inspectionDate: "2026-10-27",
    category: "Hull Erection",
    stage: "Daily booking",
    result: "Not Accepted",
    block: "UD10",
    side: "P",
    defectNoted: true,
    defectText: "Gap 4 mm at frame 108, port deck plate",
    comments: "The gap is wider than the weld procedure allows. The yard opens the joint and fits it again, then asks us for a repair check.",
    ownerRep: "Attended",
    classRep: "",
    makerRep: "Attended",
    yardQc: "Yard QC attended",
    subcontractor: "",
    capturedBy: "t.aoki@example.com",
    capturedAtUtc: "2026-10-27T08:42:00.000Z",
    approvedBy: "",
    approvedAtUtc: "",
    repairLink: "",
  },
];

const fcPhoto = (label, fill) => ({
  clientPhotoId: `ph-${label}`,
  driveItemId: "",
  caption: label,
  capturedAtUtc: "2026-10-27T08:40:00.000Z",
  url: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="${fill}"/><text x="200" y="156" font-size="22" text-anchor="middle" fill="#e2e8f0" font-family="sans-serif">${label}</text></svg>`)}`,
});

/** What the record sheet reads beside the register row in the forecast world: photos, change history and the NCR. */
export const FC_RECORD_EXTRAS = {
  2001: {
    photos: [fcPhoto("UD10 P frame 108", "#1e293b"), fcPhoto("UD10 P gap close-up", "#334155"), fcPhoto("UD10 P deck plate", "#0f172a")],
    amendments: [
      { seq: 1, fields: { defectText: "Gap 4 mm at frame 108, port deck plate" }, reason: "Frame number corrected", void: false, amendedBy: "t.aoki@example.com", amendedAtUtc: "2026-10-27T11:15:00.000Z", source: "api" },
    ],
    ncr: { code: "SS418", ncrNumber: 612, title: "Gap at frame 108, UD10 port deck plate" },
  },
  2008: { photos: [fcPhoto("BG7 P weld after repair", "#1e293b")], amendments: [], ncr: null },
  1968: { photos: [], amendments: [], ncr: null },
};

/** A review item applied to a fixture register row, as the server's read-back would show it. */
export function applyFixtureReview(row, item) {
  if (item.op === "approve") return { ...row, approvedBy: "t.aoki@example.com", approvedAtUtc: "2026-10-27T16:40:00.000Z" };
  if (item.op === "unapprove") return { ...row, approvedBy: "", approvedAtUtc: "" };
  if (item.op === "unlink_repair") return { ...row, repairLink: "none" };
  if (item.op === "link_repair") return { ...row, repairLink: String(item.target) };
  return row;
}

const dayDiff = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);

/** RAW `GET /api/cockpit/plan` for the forecast world: bookings in lanes, the forward band, later weeks, the repair count. */
export function buildFixturePlanForecastEnvelope({ rows = FC_ROWS, forecastRows = FC_STORED } = {}) {
  const dayMap = new Map();
  for (const r of forecastRows) {
    const ahead = dayDiff(FC_TODAY, r.plannedDate);
    if (ahead < 0 || ahead > 7) continue;
    if (!dayMap.has(r.plannedDate)) dayMap.set(r.plannedDate, []);
    dayMap.get(r.plannedDate).push({ code: r.code, scheduleRef: r.scheduleRef, title: r.title, block: r.block, side: r.side, division: r.category, confidence: ahead <= 2 ? "expected" : "likely" });
  }
  return {
    generatedAtUtc: "2026-10-27T09:00:00.000Z",
    weekStart: FC_WEEK_START,
    lanes: [{ upn: SMU, teams: ["SS418"] }, { upn: ASU, teams: ["SS418"] }, { upn: "", teams: [] }],
    items: rows.map((row) => ({ ...row, assignedTo: String(row.assignedTo || "").toLowerCase() })),
    unbooked: [],
    backlog: [],
    backlogTotal: 0,
    backlogOffset: 0,
    backlogLimit: 20,
    imports: [],
    errors: [],
    forecast: { days: [...dayMap.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => ({ date, items })), laterWeeks: FC_LATER },
    repairChecks: { count: FC_REPAIR_CHECKS.length, items: FC_REPAIR_CHECKS },
  };
}

const IMPORT_FIELD_PAIRS = ["title", "category", "stage", "inspectionType", "plannedDate", "witnessParties", "assignedTo", "notes"];

function normText(v) {
  return v === undefined || v === null ? "" : String(v);
}

/**
 * In-memory reimplementation of the upsert contract (schedule.js
 * `upsertScheduleItem` + CisScheduleImport's inspector-roster gate) so the
 * Plan screen's drag-and-drop can be driven end to end offline, entirely
 * within fixture mode. Not a general SharePoint simulator — just enough to
 * prove the batch envelope's per-item outcomes.
 */
export function createFixturePlanStore({ world = "base" } = {}) {
  const forecastWorld = world === "forecast";
  let rows = (forecastWorld ? FC_ROWS : INITIAL_ROWS).map((r) => ({ ...r }));
  let unbooked = (forecastWorld ? [] : UNBOOKED_ROWS).map((r) => ({ ...r }));
  // Day redesign phase 2: the yard's forward rows (Basis = forecast); a booking clears the basis.
  let forecastRows = (forecastWorld ? FC_STORED : []).map((r) => ({ ...r }));
  // Yard-schedule import (§4.3, DEC-CHAT-20260930-204) — a minimal in-memory
  // stand-in for CisScheduleImports/CisScheduleImportCommit's preview/commit
  // pair, same offline-testability role as the upsert simulator above. Not
  // a parser reimplementation: the "file" is never actually read, since the
  // cockpit's own contract is the request/response shape, not the parse.
  const importRuns = new Map();
  let importSeq = 0;

  function findRow(scheduleRef) {
    return rows.find((r) => r.scheduleRef === scheduleRef) || unbooked.find((r) => r.scheduleRef === scheduleRef) || forecastRows.find((r) => r.scheduleRef === scheduleRef);
  }

  async function fetchPlan() {
    return { status: 200, ok: true, body: forecastWorld ? buildFixturePlanForecastEnvelope({ rows, forecastRows }) : buildFixturePlanEnvelope({ rows, unbooked }) };
  }

  /** GET /api/projects/{code}/schedule?from=&to= — every stored row of the project in the range. */
  async function fetchScheduleRows(code, from, to) {
    const inRange = (r) => (!from || r.plannedDate >= from) && (!to || r.plannedDate <= to);
    return { status: 200, ok: true, body: [...rows, ...unbooked, ...forecastRows].filter((r) => r.code === code && inRange(r)).map((r) => ({ ...r })) };
  }

  async function upsertSchedule(code, body) {
    const items = (body && body.items) || [];
    const results = items.map((item) => {
      const assignedTo = item.assignedTo ? String(item.assignedTo).trim().toLowerCase() : "";
      if (assignedTo && !(ROSTER[code] || new Set()).has(assignedTo)) {
        return { scheduleRef: item.scheduleRef, status: "invalid", errors: [["assignedTo", "inspector_not_on_roster"]] };
      }
      if (item.sourceInspectionNumber && !REGISTER_NUMBERS.has(Number(item.sourceInspectionNumber))) {
        return { scheduleRef: item.scheduleRef, status: "invalid", errors: [["sourceInspectionNumber", "source_not_found"]] };
      }

      const existing = findRow(item.scheduleRef);
      if (!existing) {
        const created = scheduleRow({
          scheduleId: Math.floor(Math.random() * 100000),
          scheduleRef: item.scheduleRef,
          code,
          title: item.title,
          category: item.category,
          stage: item.stage,
          inspectionType: item.inspectionType,
          plannedDate: item.plannedDate,
          witnessParties: item.witnessParties,
          assignedTo,
          sourceInspectionNumber: item.sourceInspectionNumber,
          notes: item.notes,
        });
        if (created.plannedDate) rows.push(created);
        else unbooked.push(created);
        return { scheduleRef: item.scheduleRef, status: "created", readback: created };
      }

      const desired = { ...item, assignedTo };
      const mismatched = IMPORT_FIELD_PAIRS.filter((f) => normText(desired[f]) !== normText(existing[f]));
      // `basis: ""` is the booking of a forecast item (explicit clear); an absent basis changes nothing.
      if (item.basis !== undefined && item.basis !== null && normText(item.basis) !== normText(existing.basis)) mismatched.push("basis");
      if (mismatched.length === 0) {
        return { scheduleRef: item.scheduleRef, status: "duplicate", scheduleId: existing.scheduleId };
      }
      const editable = ["assignedTo", "plannedDate", "basis"];
      if (!mismatched.every((f) => editable.includes(f))) {
        return { scheduleRef: item.scheduleRef, status: "conflict" };
      }

      const wasUnbooked = !existing.plannedDate;
      if (mismatched.includes("assignedTo")) existing.assignedTo = assignedTo;
      if (mismatched.includes("plannedDate")) existing.plannedDate = item.plannedDate;
      if (mismatched.includes("basis")) {
        existing.basis = normText(item.basis);
        if (!existing.basis && forecastRows.includes(existing)) {
          forecastRows = forecastRows.filter((r) => r !== existing);
          rows.push(existing);
        }
      }
      if (wasUnbooked && existing.plannedDate) {
        unbooked = unbooked.filter((r) => r.scheduleRef !== existing.scheduleRef);
        rows.push(existing);
      }
      return { scheduleRef: item.scheduleRef, status: "updated", scheduleId: existing.scheduleId, readback: { ...existing } };
    });
    return { status: 200, ok: true, body: results };
  }

  async function previewScheduleImport(code, file) {
    if (!file) return { status: 400, ok: false, body: { errorCode: "multipart_invalid" } };
    importSeq += 1;
    const runId = `imp-${String(code).toLowerCase()}-fixture${importSeq}t0z`;
    // A file named like the yard's daily list (2026.10.27.xlsx) previews as one: the
    // booking rows of that day in real yard naming (Day redesign phase 1).
    if (/^\d{4}\.\d{2}\.\d{2}/.test(String(file.name || ""))) {
      const T = (a, b) => `${a}\u2013${b}`;
      const cand = (key, time, label, place, block, side) => ({ key, date: "2026-10-27", time, label, place, block, side, type: "daily", status: "new", ownerInspection: null });
      const rows = [
        cand("DB-20261027-a1f3c2-P", T("08:30", "09:00"), "UD10 \u00b7 block final inspection", "二期 assembly shop, bay 4", "UD10", "P"),
        cand("DB-20261027-a1f3c2-S", T("08:30", "09:00"), "UD10 \u00b7 block final inspection", "二期 assembly shop, bay 4", "UD10", "S"),
        cand("DB-20261027-b7d410", T("10:00", "10:30"), "BG7 \u00b7 welding inspection", "四期 assembly shop, bay 2", "BG7", ""),
        cand("DB-20261027-c93e55", T("13:30", "14:00"), "BS2-D \u00b7 steel cutting", "四期 NC workshop (FAB13), NC machine 3", "BS2-D", ""),
        cand("DB-20261027-d02a8e-S", T("14:30", "15:00"), "3D1 \u00b7 block final inspection", "二期 assembly shop, bay 6", "3D1", "S"),
      ];
      importRuns.set(runId, { code, applied: false });
      return {
        status: 200,
        ok: true,
        body: { runId, format: "daily_booking", preview: { creates: { count: rows.length, sample: [] }, skipped: { count: 2 }, candidates: { total: rows.length, truncated: false, rows }, ownerInspectionField: null } },
      };
    }
    // candidates mirrors shared/schedule-import.js buildCandidates's shape
    // (azure-functions/cis) — ledger format, so ownerInspection is always
    // null (no field in that format identifies owner attendance).
    const candidates = {
      total: 3,
      truncated: false,
      rows: [
        { key: "ITP-HE-090", date: null, label: "No.5 WBT shell plate", type: "A/T", status: "new", ownerInspection: null },
        { key: "ITP-HE-091", date: null, label: "No.5 WBT bulkhead", type: "A/T", status: "new", ownerInspection: null },
        { key: "ITP-HE-041", date: "2026-10-09", label: "No.4 BWBT closing", type: "CH", status: "changed", ownerInspection: null },
      ],
    };
    const preview = {
      creates: { count: 2, sample: ["ITP-HE-090", "ITP-HE-091"] },
      updates: { count: 1, sample: [{ itpRef: "ITP-HE-041", fields: ["plannedDate"] }] },
      retires: { count: 0, sample: [] },
      unchanged: { count: 4 },
      skipped: { count: 1, sample: ["Row 17: no date"] },
      candidates,
      ownerInspectionField: null,
    };
    importRuns.set(runId, { code, applied: false });
    return { status: 200, ok: true, body: { runId, format: "itp_ledger", preview } };
  }

  async function commitScheduleImport(code, runId, body) {
    const run = importRuns.get(runId);
    if (!run || run.code !== code) return { status: 404, ok: false, body: { errorCode: "run_not_found" } };
    run.applied = true;
    // Owner row-level selection (6 Oct 2026): an explicit (even empty)
    // `selectedKeys` narrows the fixture's own fixed candidate set the same
    // way the real commit narrows the re-derived diff — omitted keeps the
    // full-file counts below, matching the real server's documented no-op.
    const selectedKeys = body && Array.isArray(body.selectedKeys) ? body.selectedKeys : null;
    let counts = { created: 2, updated: 1, duplicate: 4, invalid: 0, error: 0, remaining: 0 };
    if (selectedKeys) {
      const created = selectedKeys.filter((k) => k === "ITP-HE-090" || k === "ITP-HE-091").length;
      const updated = selectedKeys.includes("ITP-HE-041") ? 1 : 0;
      counts = { created, updated, duplicate: 4, invalid: 0, error: 0, remaining: 0 };
    }
    return { status: 200, ok: true, body: { runId, status: "committed", counts } };
  }

  return { fetchPlan, fetchScheduleRows, upsertSchedule, previewScheduleImport, commitScheduleImport };
}

/** Presentational-only fixture metadata — not part of the cockpit data contract. */
export const fixturePlanMeta = {
  identity: { initials: "SL", role: "Site manager" },
  weekLabel: "W36 · Wed 2 Sep",
};
